import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Space, Table, Tooltip, Typography, message } from 'antd';
import { BulbOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  recommendDispatchAssignments,
  formatDispatchBreakdown,
  formatDistance,
} from '@ubus/shared';
import type { DispatchRecommendation } from '@ubus/shared';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface DispatchRecommendationListProps {
  tripId: string | undefined;
  onSelect: (vehicleId: string, driverId: string) => void;
  compact?: boolean;
  /** When set, only show recommendations whose vehicle is in this fleet. */
  fleetOperatorId?: string | null;
  allowedVehicleIds?: string[];
}

function DispatchRecommendationList({
  tripId,
  onSelect,
  compact = false,
  fleetOperatorId,
  allowedVehicleIds,
}: DispatchRecommendationListProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DispatchRecommendation[]>([]);
  const [loaded, setLoaded] = useState(false);

  const allowedSet = useMemo(
    () => new Set(allowedVehicleIds ?? []),
    [allowedVehicleIds]
  );

  const visibleRows = useMemo(() => {
    if (!fleetOperatorId) return [];
    if (allowedSet.size === 0) return [];
    return rows.filter((r) => allowedSet.has(r.vehicle_id));
  }, [allowedSet, fleetOperatorId, rows]);

  useEffect(() => {
    setLoaded(false);
    setRows([]);
  }, [fleetOperatorId, tripId]);

  const load = async () => {
    if (!tripId) return;
    if (!fleetOperatorId) {
      message.info(t('dispatchRec.selectFleetFirst'));
      return;
    }
    setLoading(true);
    try {
      const data = await recommendDispatchAssignments(tripId, compact ? 3 : 8);
      const filtered = data.filter((r) => allowedSet.has(r.vehicle_id));
      setRows(data);
      setLoaded(true);
      if (filtered.length === 0) {
        message.info(t('dispatchRec.noMatches'));
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('dispatchRec.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!tripId) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('dispatchRec.selectTrip')}
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!fleetOperatorId) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('dispatchRec.selectFleetFirst')}
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Space style={{ marginBottom: 8 }}>
        <Button
          icon={<BulbOutlined />}
          loading={loading}
          onClick={() => void load()}
        >
          {t('dispatchRec.recommend')}
        </Button>
        {loaded && (
          <Button icon={<ReloadOutlined />} type="text" size="small" onClick={() => void load()}>
            {t('dispatchRec.refresh')}
          </Button>
        )}
      </Space>

      {loaded && visibleRows.length > 0 && (
        <Table
          size="small"
          rowKey={(r) => `${r.vehicle_id}-${r.driver_id}`}
          pagination={false}
          dataSource={visibleRows}
          columns={[
            {
              title: '#',
              width: 40,
              render: (_, __, index) => index + 1,
            },
            {
              title: t('dispatchRec.vehicle'),
              render: (_, r) => (
                <Text strong>
                  {r.fleet_number}
                </Text>
              ),
            },
            {
              title: t('dispatchRec.driver'),
              dataIndex: 'driver_name',
              ellipsis: true,
            },
            {
              title: t('dispatchRec.distance'),
              width: 72,
              render: (_, r) =>
                r.distance_meters != null ? formatDistance(r.distance_meters) : '—',
            },
            {
              title: t('dispatchRec.score'),
              width: 56,
              render: (_, r) => (
                <Tooltip title={formatDispatchBreakdown(r.breakdown)}>
                  <Text>{Number(r.score).toFixed(0)}</Text>
                </Tooltip>
              ),
            },
            {
              title: '',
              width: 72,
              render: (_, r) => (
                <Button
                  type="link"
                  size="small"
                  onClick={() => onSelect(r.vehicle_id, r.driver_id)}
                >
                  {t('dispatchRec.use')}
                </Button>
              ),
            },
          ]}
        />
      )}

      {loaded && visibleRows.length === 0 && (
        <Text type="secondary">{t('dispatchRec.noEligible')}</Text>
      )}
    </div>
  );
}

export default DispatchRecommendationList;
